/**
 * Tournament sign-up widget.
 *
 * Mounted on every .big-squash-signup element: on the tournament cards of
 * [big_squash_tournaments] (initial state embedded in data-signup, so the
 * first paint needs no request) and on the bracket page while the draw is
 * pending (bracket-display.js creates the element and calls
 * window.BigSquashSignup.mount on it, with data-show-entrants="1" to list
 * who is in). Talks to /tournaments/{id}/entries; config is bigSquashSignup.
 *
 * On the day the same widget carries the "I've arrived" button: from 30
 * minutes before the start a signed-up player can check themselves in, and
 * the entry fee (and whether the desk has recorded it as paid) is shown.
 * Once the draw exists the bracket page mounts it with data-mode="day": a
 * compact strip with only the viewer's own status, hidden for everyone else.
 */

import { __, _n, sprintf, ordinal } from '../lib/i18n.js';

const cfg = () => window.bigSquashSignup || {};

function api(path, opts = {}) {
    const { restUrl, restNonce } = cfg();
    return fetch(`${restUrl}${path}`, {
        credentials: 'same-origin',
        ...opts,
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': restNonce,
            ...(opts.headers || {}),
        },
    });
}

async function readJson(res) {
    try {
        return await res.json();
    } catch {
        return null;
    }
}

function esc(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(text == null ? '' : text).replace(/[&<>"']/g, (m) => map[m]);
}

function flag(country) {
    return country ? `<span class="fi fi-${esc(String(country).toLowerCase())}" aria-hidden="true"></span> ` : '';
}

function countLine(r) {
    let text = sprintf(__('%1$d of %2$d places taken'), r.registered, r.capacity);
    if (r.waitlisted > 0) {
        text += ' · ' + sprintf(_n('%d on the waiting list', '%d on the waiting list', r.waitlisted), r.waitlisted);
    }
    return text;
}

/** Whether a "Y-m-d H:i:s" site-time string falls on the viewer's current day. */
function isToday(dateString) {
    if (!dateString) {
        return false;
    }
    const d = new Date(String(dateString).replace(' ', 'T'));
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function hasActiveEntry(v) {
    return !!(v.entry && (v.entry.status === 'registered' || v.entry.status === 'waitlisted'));
}

/** The fee line under the viewer's own entry: what they owe, or that it is settled. */
function feeText(data) {
    const r = data.registration;
    const entry = data.viewer.entry;
    if (!r.entry_fee_formatted || !hasActiveEntry(data.viewer)) {
        return null;
    }
    switch (entry.payment_status) {
        case 'paid':
            return {
                kind: 'paid',
                text: entry.paid_amount_formatted
                    ? sprintf(__('Entry fee paid (%s).'), entry.paid_amount_formatted)
                    : __('Entry fee paid.'),
            };
        case 'waived':
            return { kind: 'paid', text: __('No entry fee to pay.') };
        case 'refunded':
            return { kind: 'paid', text: __('Entry fee refunded.') };
        default:
            return { kind: 'unpaid', text: sprintf(__('Entry fee %s to pay.'), r.entry_fee_formatted) };
    }
}

/** Check-in state for a signed-up viewer: done, open, or opening later today. */
function checkInState(data) {
    const v = data.viewer;
    const r = data.registration;
    if (!hasActiveEntry(v)) {
        return null;
    }
    if (v.entry.checked_in_at) {
        return {
            kind: 'checked_in',
            text: v.entry.checked_in_at_formatted
                ? sprintf(__('Checked in at %s.'), v.entry.checked_in_at_formatted)
                : __('Checked in.'),
        };
    }
    if (v.can_check_in) {
        return { kind: 'open', text: '' };
    }
    if (v.check_in_blocker === 'not_open_yet' && r.check_in_opens_at_formatted && isToday(data.tournament && data.tournament.date)) {
        return { kind: 'soon', text: sprintf(__('Check-in opens at %s.'), r.check_in_opens_at_formatted) };
    }
    return null;
}

function closedText(r) {
    switch (r.closed_reason) {
        case 'started':
            return __('This tournament has started.');
        case 'not_yet_open':
            return r.opens_at_formatted ? sprintf(__('Sign-ups open %s.'), r.opens_at_formatted) : __('Sign-ups have not opened yet.');
        case 'deadline_passed':
            return __('Sign-ups have closed.');
        case 'draw_made':
            return __('The draw has been made; sign-ups are closed.');
        default:
            return __('Sign-ups are not open.');
    }
}

/** What the viewer sees next to the count: their own status, or why they cannot act. */
function viewerState(data) {
    const v = data.viewer;
    const r = data.registration;
    const entry = v.entry;

    if (entry && entry.status === 'registered') {
        return { kind: 'in', text: sprintf(__("You're in (place %d)."), entry.position) };
    }
    if (entry && entry.status === 'waitlisted') {
        return { kind: 'waiting', text: sprintf(__("You're %s on the waiting list."), ordinal(entry.position)) };
    }
    if (entry && entry.status === 'declined') {
        return { kind: 'declined', text: __('Your entry was declined by the organiser.') };
    }
    if (!r.open) {
        return { kind: r.closed_reason === 'not_yet_open' ? 'soon' : 'closed', text: closedText(r) };
    }
    if (!v.logged_in) {
        return { kind: 'login', text: '' };
    }
    if (!v.has_player) {
        return { kind: 'no_player', text: '' };
    }
    if (v.blocker && !v.can_sign_up) {
        return { kind: 'blocked', text: v.blocker_message || __('You cannot sign up for this tournament.') };
    }
    return { kind: 'open', text: r.closes_at_formatted ? sprintf(__('Sign-ups close %s.'), r.closes_at_formatted) : '' };
}

function entrantsHtml(data) {
    const cap = data.registration.capacity;
    const base = cfg().playerProfileBase || '';
    const registered = data.entries.filter((e) => e.status === 'registered');
    const waiting = data.entries.filter((e) => e.status === 'waitlisted');

    if (!data.entries.length) {
        return `<p class="big-squash-signup__empty">${esc(__('No one has signed up yet.'))}</p>`;
    }

    const item = (e) => {
        const name = e.slug && base
            ? `<a href="${esc(base + e.slug)}">${esc(e.full_name)}</a>`
            : esc(e.full_name);
        return `<li class="big-squash-signup__entrant">${flag(e.country)}${name}</li>`;
    };

    let html = `<div class="big-squash-signup__entrants">
        <h4>${esc(sprintf(__('Entered (%1$d/%2$d)'), registered.length, cap))}</h4>
        <ol>${registered.map(item).join('')}</ol>`;
    if (waiting.length) {
        html += `<h4>${esc(sprintf(__('Waiting list (%d)'), waiting.length))}</h4><ol>${waiting.map(item).join('')}</ol>`;
    }
    return html + '</div>';
}

/** Day mode: the viewer's own status on the bracket page; nothing for anyone without an entry. */
function renderDay(el, data) {
    const v = data.viewer;
    const t = data.tournament || {};
    if (!hasActiveEntry(v) || t.status === 'completed') {
        el.hidden = true;
        el.innerHTML = '';
        return;
    }
    const fee = feeText(data);
    const checkIn = checkInState(data);
    const stateText = v.entry.status === 'waitlisted'
        ? sprintf(__("You're %s on the waiting list."), ordinal(v.entry.position))
        : __("You're in.");
    const actions = v.can_check_in
        ? `<button type="button" class="big-squash-signup__button big-squash-signup__button--checkin" data-signup-action="checkin">${esc(__("I've arrived"))}</button>`
        : '';

    el.hidden = false;
    el.innerHTML = `
        <div class="big-squash-signup__bar">
            <span class="big-squash-signup__state big-squash-signup__state--in">${esc(stateText)}</span>
            ${fee ? `<span class="big-squash-signup__fee-state is-${fee.kind}">${esc(fee.text)}</span>` : ''}
            ${checkIn && checkIn.text ? `<span class="big-squash-signup__state big-squash-signup__state--${checkIn.kind}">${esc(checkIn.text)}</span>` : ''}
        </div>
        ${actions ? `<div class="big-squash-signup__actions" data-signup-actions>${actions}</div>` : ''}
        <p class="big-squash-signup__message" data-signup-message hidden></p>`;
}

function render(el, data) {
    if (el.dataset.mode === 'day') {
        renderDay(el, data);
        return;
    }
    const v = data.viewer;
    const r = data.registration;
    const c = cfg();
    const state = viewerState(data);
    const fee = feeText(data);
    const checkIn = checkInState(data);
    const full = r.registered >= r.capacity;

    let actions = '';
    if (v.can_check_in) {
        actions += `<button type="button" class="big-squash-signup__button big-squash-signup__button--checkin" data-signup-action="checkin">${esc(__("I've arrived"))}</button>`;
    }
    if (v.can_sign_up) {
        actions = `<button type="button" class="big-squash-signup__button big-squash-signup__button--primary" data-signup-action="signup">${esc(full ? __('Join the waiting list') : __('Sign up'))}</button>`;
    } else if (v.can_withdraw) {
        actions += `<button type="button" class="big-squash-signup__button" data-signup-action="withdraw">${esc(v.entry.status === 'waitlisted' ? __('Leave the waiting list') : __('Withdraw'))}</button>`;
    } else if (state.kind === 'login') {
        actions = `<a href="${esc(c.loginUrl || '#')}" class="big-squash-signup__button big-squash-signup__button--primary big-squash-open-login">${esc(__('Log in to sign up'))}</a>`;
    } else if (state.kind === 'no_player') {
        actions = `<a href="${esc(c.registerUrl || '#')}" class="big-squash-signup__button big-squash-signup__button--primary">${esc(__('Complete your profile to sign up'))}</a>`;
    }

    const noteForm = v.can_sign_up ? `
        <div class="big-squash-signup__note" data-signup-note-form hidden>
            <label>
                <span>${esc(__('Note to the organiser (optional)'))}</span>
                <textarea maxlength="500" rows="2" data-signup-note placeholder="${esc(__('e.g. I can only play after 11:00'))}"></textarea>
            </label>
            <div class="big-squash-signup__note-actions">
                <button type="button" class="big-squash-signup__button big-squash-signup__button--primary" data-signup-action="confirm">${esc(full ? __('Join the waiting list') : __('Confirm sign-up'))}</button>
                <button type="button" class="big-squash-signup__button" data-signup-action="cancel">${esc(__('Cancel'))}</button>
            </div>
        </div>` : '';

    const note = v.entry && v.entry.note && (v.entry.status === 'registered' || v.entry.status === 'waitlisted')
        ? `<p class="big-squash-signup__own-note">${esc(sprintf(__('Your note: %s'), v.entry.note))}</p>`
        : '';

    // Withdrawing asks for an optional reason in place of a bare confirm
    const leaving = v.can_withdraw && v.entry.status === 'waitlisted';
    const withdrawForm = v.can_withdraw ? `
        <div class="big-squash-signup__note" data-signup-withdraw-form hidden>
            <label>
                <span>${esc(leaving ? __('Leave the waiting list?') : __('Withdraw from this tournament?'))} ${esc(__('Let the organiser know why (optional)'))}</span>
                <textarea maxlength="500" rows="2" data-signup-withdraw-note placeholder="${esc(__("e.g. injured, or can't make the date"))}"></textarea>
            </label>
            <div class="big-squash-signup__note-actions">
                <button type="button" class="big-squash-signup__button big-squash-signup__button--primary" data-signup-action="confirm-withdraw">${esc(leaving ? __('Leave the waiting list') : __('Withdraw'))}</button>
                <button type="button" class="big-squash-signup__button" data-signup-action="cancel">${esc(__('Cancel'))}</button>
            </div>
        </div>` : '';

    el.innerHTML = `
        <div class="big-squash-signup__bar">
            <span class="big-squash-signup__count">${esc(countLine(r))}</span>
            ${r.entry_fee_formatted ? `<span class="big-squash-signup__fee">${esc(sprintf(__('Entry fee %s'), r.entry_fee_formatted))}</span>` : ''}
            ${state.text ? `<span class="big-squash-signup__state big-squash-signup__state--${state.kind}">${esc(state.text)}</span>` : ''}
            ${checkIn && checkIn.text ? `<span class="big-squash-signup__state big-squash-signup__state--${checkIn.kind}">${esc(checkIn.text)}</span>` : ''}
        </div>
        ${fee ? `<p class="big-squash-signup__fee-state is-${fee.kind}">${esc(fee.text)}</p>` : ''}
        ${note}
        ${actions ? `<div class="big-squash-signup__actions" data-signup-actions>${actions}</div>` : ''}
        ${noteForm}
        ${withdrawForm}
        <p class="big-squash-signup__message" data-signup-message hidden></p>
        ${el.dataset.showEntrants === '1' ? entrantsHtml(data) : ''}`;
}

function showMessage(el, text, isError) {
    const box = el.querySelector('[data-signup-message]');
    if (!box) {
        return;
    }
    box.textContent = text;
    box.classList.toggle('is-error', !!isError);
    box.hidden = !text;
}

async function request(el, method, body, suffix = '') {
    const id = el.dataset.tournamentId;
    const path = `/tournaments/${id}/entries${suffix}`;
    const res = await api(path, { method, body: body ? JSON.stringify(body) : undefined });
    const data = await readJson(res);
    if (!res.ok) {
        throw new Error((data && data.message) || __('Something went wrong. Please try again.'));
    }
    return data;
}

function bind(el) {
    el.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-signup-action]');
        if (!btn || !el.contains(btn)) {
            return;
        }
        const action = btn.dataset.signupAction;
        const noteForm = el.querySelector('[data-signup-note-form]');
        const withdrawForm = el.querySelector('[data-signup-withdraw-form]');
        const actionsBox = el.querySelector('[data-signup-actions]');

        const openForm = (form, areaSelector) => {
            if (!form) {
                return;
            }
            form.hidden = false;
            if (actionsBox) actionsBox.hidden = true;
            const area = el.querySelector(areaSelector);
            if (area) area.focus();
        };

        if (action === 'signup') {
            openForm(noteForm, '[data-signup-note]');
            return;
        }
        if (action === 'withdraw') {
            openForm(withdrawForm, '[data-signup-withdraw-note]');
            return;
        }
        if (action === 'cancel') {
            if (noteForm) noteForm.hidden = true;
            if (withdrawForm) withdrawForm.hidden = true;
            if (actionsBox) actionsBox.hidden = false;
            return;
        }

        let pending;
        if (action === 'confirm') {
            const area = el.querySelector('[data-signup-note]');
            pending = request(el, 'POST', { note: area ? area.value : '' });
        } else if (action === 'confirm-withdraw') {
            const area = el.querySelector('[data-signup-withdraw-note]');
            pending = request(el, 'DELETE', { note: area ? area.value : '' }, '/me');
        } else if (action === 'checkin') {
            pending = request(el, 'POST', null, '/me/check-in');
        } else {
            return;
        }

        el.classList.add('is-busy');
        try {
            const data = await pending;
            render(el, data);
            const status = data.viewer.entry ? data.viewer.entry.status : null;
            let text;
            if (action === 'confirm-withdraw') {
                text = __('You have withdrawn.');
            } else if (action === 'checkin') {
                text = __("You're checked in. See you on court!");
            } else {
                text = status === 'waitlisted'
                    ? __("You're on the waiting list. We'll email you if a place opens up.")
                    : __("You're in! A confirmation is on its way to your inbox.");
            }
            showMessage(el, text);
        } catch (err) {
            showMessage(el, err.message, true);
        } finally {
            el.classList.remove('is-busy');
        }
    });
}

async function refresh(el) {
    try {
        const res = await api(`/tournaments/${el.dataset.tournamentId}/entries`);
        const data = await readJson(res);
        if (!res.ok) {
            throw new Error((data && data.message) || __('Could not load sign-ups.'));
        }
        render(el, data);
    } catch (err) {
        if (el.dataset.mode === 'day') {
            el.hidden = true;
            return;
        }
        el.innerHTML = `<p class="big-squash-signup__message is-error">${esc(err.message)}</p>`;
    }
}

function mount(el) {
    if (!el || el.dataset.signupMounted === '1' || !el.dataset.tournamentId) {
        return;
    }
    el.dataset.signupMounted = '1';
    bind(el);

    if (el.dataset.signup) {
        try {
            render(el, JSON.parse(el.dataset.signup));
            return;
        } catch {
            // fall through to a fetch
        }
    }
    refresh(el);
}

function mountAll(root) {
    (root || document).querySelectorAll('.big-squash-signup').forEach(mount);
}

window.BigSquashSignup = { mount, mountAll, refresh };

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
    mountAll();
}
